import {
  Button,
  makeStyles,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import React, { useState, useEffect } from "react";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import useToggle from "../../../state/useToggle";
import { useToastError } from "../../../components/FormError";
import api from "../../../api";
import { toast } from "react-toastify";

export default function FillCustomerIdentityViaCsv({ partnerId }) {
  const classes = useStyles();
  const toggle = useToggle();
  const toastError = useToastError();
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [response, setResponse] = useState("");
  const [dayFirstInDate, setDayFirstInDate] = useState(false);

  useEffect(() => {
    if (toggle.on) {
      setFile(null);
      setResponse("");
      setIsSubmitting(false);
      setDayFirstInDate(false);
    }
  }, [toggle.on]);

  const handleFileChange = (event) => {
    const [selectedFile] = event.target.files || [];

    if (!selectedFile || selectedFile.type !== "text/csv") {
      toastError(new Error("Please select a valid CSV file"));
      event.target.value = null;
      return;
    }

    setFile(selectedFile);
    setResponse("");
  };

  const formatResponse = (data) => {
    // Handle both camelCase (from API transformation) and snake_case (direct from backend)
    const jobsEnqueued = data?.jobsEnqueued ?? data?.jobs_enqueued ?? 0;
    const invalidRows = data?.invalidRows ?? data?.invalid_rows ?? 0;
    const message = data?.message ?? "Processing started";

    let responseText = `${message}\n\n`;
    responseText += `Jobs Enqueued: ${jobsEnqueued}\n`;
    responseText += `Invalid Rows Skipped: ${invalidRows}\n\n`;
    responseText += `Note: Jobs are processed asynchronously.\n`;
    responseText += `Check #admin-alerts on Slack for any failures.`;

    return responseText;
  };

  const handleSubmit = async () => {
    if (!file) {
      toastError(new Error("Please select a CSV file to upload"));
      return;
    }

    setIsSubmitting(true);
    setResponse("");

    try {
      const formData = new FormData();
      formData.append("csv", file);
      formData.append("partner_id", partnerId);
      if (dayFirstInDate) {
        formData.append("day_first_in_date", "true");
      }

      const result = await api.fillCustomerIdentityViaCsv(formData);
      const data = result.data || {};

      const formattedResponse = formatResponse(data);
      setResponse(formattedResponse);

      // Handle both camelCase and snake_case
      const jobsEnqueued = data?.jobsEnqueued ?? data?.jobs_enqueued ?? 0;
      const invalidRows = data?.invalidRows ?? data?.invalid_rows ?? 0;

      if (invalidRows === 0) {
        toast.success(
          `Enqueued ${jobsEnqueued} job(s) for processing. Check Slack for results.`
        );
      } else {
        toast.warning(
          `Enqueued ${jobsEnqueued} job(s), skipped ${invalidRows} invalid row(s).`
        );
      }

      setFile(null);
    } catch (error) {
      const serverMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.messages?.join("\n") ||
        error.message ||
        "An error occurred while processing the file";

      toastError(serverMessage);
      setResponse(serverMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={classes.buttonGroup}>
      <Button
        color="secondary"
        variant="contained"
        className={classes.action}
        onClick={toggle.toggle}
      >
        Fill Customer Identity via CSV
      </Button>

      <Typography>
        Upload CSV with customer_id, SSN, and DOB columns to fill customer identities
      </Typography>

      <ConfirmationDialog
        open={toggle.on}
        onCancel={toggle.turnOff}
        onConfirm={handleSubmit}
        title="Fill Customer Identity via CSV"
        cancelProps={{ children: "Close" }}
        confirmProps={{
          children: isSubmitting ? "Submitting..." : "Submit",
          disabled: isSubmitting || !file,
        }}
      >
        <div className={classes.modalContent}>
          <div className={classes.modalInputs}>
            <Typography>
              Upload a CSV file with <strong>customer_id</strong>, <strong>SSN</strong>, and{" "}
              <strong>DOB</strong> columns. The system will find or create identities for each
              customer and update their SSN and DOB information.
            </Typography>

            <Typography variant="body2" color="textSecondary">
              CSV format: The file should have headers "customer_id", "SSN", and "DOB" (or "dob").
              Each row should contain a customer_id, corresponding SSN value, and DOB value.
            </Typography>

            <div>
              <input
                accept=".csv"
                className={classes.fileInput}
                id="csv-upload-identity"
                type="file"
                onChange={(e) => {
                  handleFileChange(e);
                  e.target.value = "";
                }}
              />
              <label htmlFor="csv-upload-identity">
                <Button variant="outlined" component="span">
                  {file ? file.name : "Upload CSV"}
                </Button>
              </label>
            </div>

            <FormControlLabel
              control={
                <Checkbox
                  checked={dayFirstInDate}
                  onChange={(e) => setDayFirstInDate(e.target.checked)}
                  color="primary"
                />
              }
              label="Convert DD/MM/YYYY dates to MM/DD/YYYY format"
            />
            <Typography variant="body2" color="textSecondary">
              Enable this if your CSV uses day-first date format (DD/MM/YYYY or DD-MM-YYYY). The
              system will automatically convert these dates to month-first format (MM/DD/YYYY or
              MM-DD-YYYY).
            </Typography>

            {response && (
              <div>
                <Typography variant="subtitle1">Processing Results:</Typography>
                <TextField
                  multiline
                  fullWidth
                  minRows={10}
                  value={response}
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                    className: classes.responseText,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </ConfirmationDialog>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  action: {
    marginRight: theme.spacing(2),
    boxSizing: "content-box",
  },
  buttonGroup: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  modalContent: {
    padding: theme.spacing(2),
  },
  modalInputs: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  responseText: {
    fontFamily: "monospace",
    backgroundColor: theme.palette.grey[50],
  },
  fileInput: {
    display: "none",
  },
  uploadButton: {
    minWidth: 200,
  },
}));

